import { dataService } from '../services/dataService.js';
import { seoService } from '../services/seoService.js';

export async function renderSongView(songId) {
  const container = document.createElement('div');
  container.className = 'main-content';

  // If no songId, render the all songs directory
  if (!songId) {
    seoService.update({
      title: 'Sri Lankan Movie Soundtracks & Lyrics',
      description: 'Explore Sinhala movie songs, original soundtracks, lyrics, and artists on CinemaHala.'
    });

    container.innerHTML = `
      <div class="section-header">
        <h1 class="section-title">Soundtrack & Lyrics Hub</h1>
      </div>
      <p style="color: var(--text-secondary); margin-bottom: var(--space-lg); font-size: 0.95rem;">
        Discover full Sinhala lyrics, vocalists, music directors, and official videos from iconic Sri Lankan films.
      </p>

      <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: var(--space-md);">
        <a href="#/song/gal-lena-bindala" class="song-card clickable-item" style="padding: 16px;">
          <div class="song-icon" style="width: 50px; height: 50px;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>
          </div>
          <div class="song-info">
            <h3 class="song-title">Gal Lena Bindala</h3>
            <div style="color: var(--accent-gold); font-size: 0.85rem; font-weight: 600;">ගල් ලෙන බිඳලා • Sinhabahu (2024)</div>
            <div class="song-artists" style="margin-top: 4px;">Singers: W.D. Amaradeva, Nanda Malini</div>
          </div>
          <span class="badge-pill badge-gold">Lyrics</span>
        </a>

        <a href="#/song/sandakada-pahana" class="song-card clickable-item" style="padding: 16px;">
          <div class="song-icon" style="width: 50px; height: 50px;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>
          </div>
          <div class="song-info">
            <h3 class="song-title">Sandakada Pahana</h3>
            <div style="color: var(--accent-gold); font-size: 0.85rem; font-weight: 600;">සඳකඩ පහණ • Nidhanaya (1972)</div>
            <div class="song-artists" style="margin-top: 4px;">Singer: Sunil Edirisinghe</div>
          </div>
          <span class="badge-pill badge-gold">Lyrics</span>
        </a>
      </div>
    `;
    return container;
  }

  // Detailed Song View
  const song = await dataService.getSong(songId);
  if (!song) {
    container.innerHTML = `<div style="text-align: center; padding: var(--space-2xl) 0;"><p>Song not found.</p><a href="#/songs" class="btn-primary" style="margin-top: 16px;">Back to Soundtracks</a></div>`;
    return container;
  }

  seoService.update({
    title: `${song.title} Lyrics (${song.movieTitle})`,
    description: `Read Sinhala and English transliterated lyrics for ${song.title} from ${song.movieTitle}. Music by ${song.composer}.`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'MusicRecording',
      'name': song.title,
      'alternateName': song.sinhalaTitle,
      'byArtist': (song.singers || []).map(s => ({ '@type': 'Person', 'name': s })),
      'inAlbum': {
        '@type': 'MusicAlbum',
        'name': `${song.movieTitle} Soundtrack`
      }
    }
  });

  container.innerHTML = `
    <!-- Header -->
    <div style="margin-bottom: var(--space-lg);">
      <a href="#/movie/${song.movieId}" class="badge-pill badge-gold" style="margin-bottom: 8px;">
        🎬 Movie: ${song.movieTitle} (${song.movieYear})
      </a>
      <h1 style="font-size: 1.8rem; font-weight: 800; line-height: 1.2;">${song.title}</h1>
      <div style="font-size: 1.15rem; color: var(--accent-gold); font-weight: 600; margin-top: 4px;">${song.sinhalaTitle}</div>
      <div style="font-size: 0.85rem; color: var(--text-muted); margin-top: 6px;">
        Vocalists: <strong>${(song.singers || []).join(', ')}</strong> • Composer: <strong>${song.composer}</strong>
      </div>
    </div>

    <!-- Embedded Video / Audio Player -->
    ${song.youtubeUrl ? `
      <div style="width: 100%; aspect-ratio: 16 / 9; max-height: 380px; background: #000; border-radius: var(--radius-md); overflow: hidden; margin-bottom: var(--space-xl); border: 1px solid var(--border-subtle);">
        <iframe width="100%" height="100%" src="${song.youtubeUrl}" title="${song.title}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
      </div>
    ` : ''}

    <!-- Dual-Script Lyrics Viewer -->
    <section style="background: var(--bg-surface); border: 1px solid var(--border-subtle); border-radius: var(--radius-lg); padding: var(--space-lg); margin-bottom: var(--space-xl);">
      <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--border-subtle); padding-bottom: var(--space-md); margin-bottom: var(--space-md);">
        <h2 style="font-size: 1.1rem; font-weight: 700;">Full Song Lyrics</h2>
        <div style="display: flex; gap: 4px; background: var(--bg-surface-elevated); padding: 4px; border-radius: var(--radius-full);">
          <button id="toggle-sinhala-btn" class="badge-pill clickable-item badge-gold" style="border: none; cursor: pointer;">සිංහල අකුරින්</button>
          <button id="toggle-english-btn" class="badge-pill clickable-item" style="border: none; cursor: pointer;">English / Singlish</button>
        </div>
      </div>

      <pre id="lyrics-display" style="font-family: inherit; font-size: 1.05rem; line-height: 1.8; color: var(--text-primary); white-space: pre-wrap;">${song.lyricsSinhala || 'Lyrics not available.'}</pre>
    </section>
  `;

  const sinhalaBtn = container.querySelector('#toggle-sinhala-btn');
  const englishBtn = container.querySelector('#toggle-english-btn');
  const lyricsDisplay = container.querySelector('#lyrics-display');

  sinhalaBtn.addEventListener('click', () => {
    lyricsDisplay.textContent = song.lyricsSinhala || '';
    sinhalaBtn.classList.add('badge-gold');
    englishBtn.classList.remove('badge-gold');
  });

  englishBtn.addEventListener('click', () => {
    lyricsDisplay.textContent = song.lyricsEnglish || '';
    englishBtn.classList.add('badge-gold');
    sinhalaBtn.classList.remove('badge-gold');
  });

  return container;
}
